import React from 'react';
import { Check, Minus, XCircle } from 'lucide-react';
import { TableCell, TableRow } from "@/components/ui/table";
import { Discount } from './types/planTypes';
import { formatCurrency } from "@/utils/formatters";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";

interface DiscountRowProps {
  discount: Discount;
  planId: string | undefined | null;
  planName: string | null;
  quarterlyVersions: string[];
  isDiscountInVersion?: (planId: string | null | undefined, discountId: string | null | undefined, version: string) => boolean;
  isScreenshotInvalid: (version: string) => boolean;
  openImageModal: (version: string, planName: string | null) => void;
  isLegacyPlan?: (planId: string) => boolean;
  showCurrentOnly: boolean;
  openDiscountMappingDialog?: (discount: Discount, planId: string, planName: string, version: string) => void;
}

const DiscountRow: React.FC<DiscountRowProps> = ({
  discount,
  planId,
  planName,
  quarterlyVersions,
  isDiscountInVersion,
  isScreenshotInvalid,
  openImageModal,
  isLegacyPlan,
  showCurrentOnly,
  openDiscountMappingDialog
}) => {
  // Calculate the latest version once for reuse
  const latestVersion = quarterlyVersions.length > 0 ?
    [...quarterlyVersions].sort((a, b) => parseInt(b) - parseInt(a))[0] : 
    null;
  // Format amount based on amount_type
  const formattedAmount = discount.amount_type === 'percent' 
    ? `${discount.amount}%` 
    : discount.amount ? formatCurrency(discount.amount, 'USD') : '';
    
  // Debug logging to verify formatting
  console.log(`💰 Discount ${discount.id}: amount_type="${discount.amount_type}", amount="${discount.amount}", formatted="${formattedAmount}"`);
    
  const handleClickMappingIcon = (version: string) => {
    if (openDiscountMappingDialog && planId && planName) {
      openDiscountMappingDialog(discount, planId, planName, version);
    }
  };

  const openEdit = () => {
    if (
      openDiscountMappingDialog &&
      planId &&
      planName &&
      quarterlyVersions.length > 0
    ) {
      openDiscountMappingDialog(discount, planId, planName, quarterlyVersions[0]);
    }
  };

  return (
    <TableRow className="bg-muted/20 h-14">
      <TableCell
        className="pl-8 font-normal text-muted-foreground cursor-pointer"
        colSpan={4}
        onClick={openEdit}
      >
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <div className="flex items-center">
                <div className="flex flex-col w-full">
                  <div className="flex items-center space-x-2">
                    <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
                      Discount
                    </Badge>
                    <span className="font-medium">
                      {(discount.description && discount.description.length > 30) 
                        ? `${discount.description.substring(0, 30)}...` 
                        : (discount.description || 'Unnamed discount')}
                    </span>
                  </div>
                </div>
              </div>
            </TooltipTrigger>
            <TooltipContent className="w-80 p-4">
              <div className="space-y-2">
                <div className="font-medium text-sm">Discount Details</div>
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div>
                    <span className="text-muted-foreground">Type:</span> {discount.type || 'Not specified'}
                  </div>
                  <div>
                    <span className="text-muted-foreground">Amount:</span> {formattedAmount || 'Not specified'}
                  </div>
                  {discount.duration && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Duration:</span> {discount.duration}
                    </div>
                  )}
                  {discount.description && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Description:</span> {discount.description}
                    </div>
                  )}
                  {discount.applicable_charge_types && discount.applicable_charge_types.length > 0 && (
                    <div className="col-span-2">
                      <span className="text-muted-foreground">Applies to:</span> {discount.applicable_charge_types.join(', ')}
                    </div>
                  )}
                  {discount.id && (
                    <div className="col-span-2 text-xs text-muted-foreground">
                      ID: {discount.id.substring(0, 8)}...
                    </div>
                  )}
                </div>
              </div>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </TableCell>
      
      {/* Latest column */}
      <TableCell 
        className="text-center cursor-pointer hover:bg-blue-50"
        onClick={() => {
          // Show screenshot if available
          if (latestVersion) {
            openImageModal(latestVersion, `${planName} - Discount (Latest)`);
          }
        }}
        onContextMenu={(e) => {
          // Right-click - show mapping dialog
          e.preventDefault();
          if (latestVersion && openDiscountMappingDialog && planId && planName) {
            openDiscountMappingDialog(discount, planId, planName, latestVersion);
          }
        }}
      >
        {(() => {
          const isAvailable = latestVersion && isDiscountInVersion ? 
            isDiscountInVersion(planId, discount.id, latestVersion) : 
            false;
          
          if (isAvailable) {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex justify-center">
                      {isScreenshotInvalid(latestVersion) ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isScreenshotInvalid(latestVersion) ? 
                      "Invalid screenshot - click to view" : 
                      "Discount exists in latest version - click to view screenshot, right-click to edit mapping"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          } else {
            return (
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex justify-center">
                      <Minus className="h-4 w-4 text-gray-300" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Discount does not exist in latest version - right-click to edit mapping
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            );
          }
        })()}
      </TableCell>
      
      {/* Version cells */}
      {quarterlyVersions.map(version => {
        const isAvailable = isDiscountInVersion ? isDiscountInVersion(planId, discount.id, version) : false;
        
        // Don't return null as it will skip cells, which breaks table alignment
        // Instead, still render the cell but perhaps with different content
        return (
          <TableCell 
            key={version} 
            className={cn(
              "text-center cursor-pointer", 
              (showCurrentOnly && !isAvailable) ? "" : "hover:bg-blue-50"
            )}
            onClick={(e) => {
              // If right-click or ctrl+click, show mapping dialog
              if (e.ctrlKey || e.button === 2 || e.altKey) {
                e.preventDefault();
                e.stopPropagation();
                if (openDiscountMappingDialog && planId && planName) {
                  openDiscountMappingDialog(discount, planId, planName, version);
                }
              } else {
                // Normal click - always show image modal, regardless of whether discount is available
                openImageModal(version, planName);
              }
            }}
            onContextMenu={(e) => {
              // Right-click - show mapping dialog
              e.preventDefault();
              if (openDiscountMappingDialog && planId && planName) {
                openDiscountMappingDialog(discount, planId, planName, version);
              }
            }}
          >
            {isAvailable ? (
              // Show checkmark for available versions
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex justify-center">
                      {isScreenshotInvalid(version) ? (
                        <XCircle className="h-4 w-4 text-red-600" />
                      ) : (
                        <Check className="h-4 w-4 text-green-600" />
                      )}
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    {isScreenshotInvalid(version) ? 
                      "Invalid screenshot - click to view" : 
                      "Discount exists in this version - click to view screenshot, right-click to edit mapping"}
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            ) : (
              // Show minus icon with discount value for unavailable versions
              <TooltipProvider>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <div className="flex justify-center">
                      <Minus className="h-4 w-4 text-gray-300" />
                    </div>
                  </TooltipTrigger>
                  <TooltipContent>
                    Discount does not exist in this version - right-click to edit mapping
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            )}
          </TableCell>
        );
      })}
      {/* Actions column to match the table structure */}
      <TableCell className="text-right pr-4">
        <div className="flex justify-end">
          {/* We could add actions here if needed */}
        </div>
      </TableCell>
    </TableRow>
  );
};

export default DiscountRow;
